<script setup lang="ts">
import { computed } from 'vue';
import { classNames } from '@/js/lib/bem';

export type BaseTooltipPlacement =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

export type BaseTooltipInteraction = 'hover' | 'click';
export type BaseTooltipTheme = 'tooltip' | 'popover';

const props = withDefaults(
  defineProps<{
    open?: boolean
    interaction?: BaseTooltipInteraction
    placement?: BaseTooltipPlacement
    distance?: number
    disabled?: boolean
    theme?: BaseTooltipTheme
  }>(),
  {
    open: false,
    interaction: 'hover',
    placement: 'top',
    distance: 8,
    disabled: false,
  },
);

const emit = defineEmits<{
  'update:open': [value: boolean]
}>();

const cn = classNames('base-tooltip');

const resolvedTheme = computed<BaseTooltipTheme>(() =>
  props.theme ?? (props.interaction === 'click' ? 'popover' : 'tooltip'),
);

const isShown = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
});
</script>

<template>
  <VTooltip
    v-if="interaction === 'hover'"
    :class="cn()"
    :theme="resolvedTheme"
    :placement="placement"
    :distance="distance"
    :disabled="disabled"
  >
    <slot />

    <template #popper>
      <slot name="content" />
    </template>
  </VTooltip>

  <VDropdown
    v-else
    v-model:shown="isShown"
    :class="cn()"
    :theme="resolvedTheme"
    :placement="placement"
    :distance="distance"
    :disabled="disabled"
  >
    <slot />

    <template #popper="slotProps">
      <slot name="content" v-bind="slotProps" />
    </template>
  </VDropdown>
</template>

<style lang="scss">
.v-popper--theme-popover {
  .v-popper__inner {
    padding: 0;
    border: none;
    border-radius: 4px;
    box-shadow: var(--shadow);
  }

  .v-popper__arrow-container {
    display: none;
  }
}
</style>
